/**
 * acequia.createGrantManager() — an app's grant flow, in the library
 * (unified-token-system, Phase 4).
 *
 * An app that lets peers ask for access — a camera, a debug console — gave
 * each a copy of the same flow: a viewer asks, the owner is prompted, a token
 * is minted and delivered, a returning viewer is let back in, grants renew
 * and are revoked. The manager is that flow once; the app supplies the
 * prompt and what its grants mean.
 *
 *   request    an accessRequest (peer message) or POST auth/request (HTTP)
 *              → a live grant: sent again; lapsed with auto-renew: renewed
 *              → a denied requester: refused without asking, counted
 *              → otherwise pending (persisted), and the app's onRequest asked
 *   grant      grant(key, { preset, ttlSeconds, autoRenew }) — an anchor-model
 *              grant (acequia.grants: offline, unregistered owners too) bound to
 *              the app by its audience, mirrored as a peer-token record under
 *              the leaf's id, delivered with its grant set and anchors
 *   deny       deny(key) — refused now and later (the denied list);
 *              allowAsking(key) — back to pending
 *   verify     verify(tokenOrId, { anchors }) — peer-token checks, audience,
 *              and for an anchor-model grant its anchor (revocation, scope)
 *   renew      its own engine, for this app's grants only (peerTokens'
 *              page-wide engine renewed every app's tokens with whichever
 *              app's mint function was registered last); a renewal is pushed
 *              to the viewer
 *   revoke     revoke(key) — the record and the anchor, the viewer told
 *   identity   who asked (@handle, or a guest's device name and short id —
 *              viewerIdentity.js) kept on the grant, with its last use
 *   HTTP       routes(prefix) — auth/request, present, verify, status; and
 *              auth/grants, auth/grant/:key for the owner only
 *
 * Events (EventTarget): 'request', 'grant', 'deny', 'revoke', 'renew',
 * 'deliver' (a grant handed to a viewer: granted now, or a returning one),
 * 'change' (any of the lists). Everything in the lists is plain data, for the
 * components (<acequia-grant-manager>).
 *
 * An app whose grants vary beyond its presets (GeoCamera: a preset over
 * chosen cameras) answers the prompt with a `scope` of its own beside the
 * preset, decides with `needsPrompt` when a viewer with a live grant is asked
 * about again (an upgrade), and adds to the access response with
 * `responseFields`.
 */

import { buildAccessResponse, buildTokenRenewal, buildTokenRevocation } from './tokenProtocol.js'
import * as peerTokens from './peerTokens.js'
import * as grants from './grants.js'
import { identityOf, grantRecordName } from './viewerIdentity.js'
import { ownUserIds } from './identities.js'
import { canonicalizeScope, checkScopeAccess } from './chains.js'
import { acequiaReady, getDeviceId, getInstanceId, getUserId } from '../acequia/acequia.js'

const PENDING_MAX_AGE = 24 * 60 * 60 * 1000
const USE_WRITE_INTERVAL = 60 * 1000
const MINTED = { origin: 'minted' } // every store call names the grantor's store (see peerTokens.findById)

function hasStorage() {
    try { return typeof localStorage !== 'undefined' && !!localStorage } catch { return false }
}

/**
 * The fields of an access request that belong to the protocol or to this
 * manager. Everything else a requester sends is the app's own: what
 * grantClient.request(peer, { extra }) spreads into the message.
 */
const PROTOCOL_FIELDS = new Set([
    'type', 'aud', 'scope', 'requestId', 'instanceId',
    'deviceId', 'userId', 'viewerId', 'viewerName', 'handle', 'userDisplayName', 'deviceName', 'displayName',
    'camera', 'upgradeFrom', 'token', 'anchors',
    'key', 'preset', 'requestedAt', 'identity', 'existing', 'isUpgrade',
    '__proto__', 'constructor', 'prototype',
])
const MAX_APP_FIELDS = 16
const MAX_APP_FIELD_LENGTH = 512

/**
 * An app's own request fields, passed to its onRequest as they came.
 *
 * Until 3.0.72 the request was rebuilt from a fixed list and anything else
 * was dropped, so an app could send a field and never receive it: Room
 * Painter's invitation code rode the request and arrived as undefined, and a
 * phone on another account waited for an approval that could not come.
 * Primitives only and size-capped, because a pending request is persisted;
 * never a protocol or manager field, which the request object sets itself.
 * These are the requester's claims, unverified, like its display name.
 */
function appFieldsOf(data) {
    const out = {}
    let n = 0
    for (const [k, v] of Object.entries(data || {})) {
        if (n >= MAX_APP_FIELDS || PROTOCOL_FIELDS.has(k)) continue
        const ok = typeof v === 'string' ? v.length <= MAX_APP_FIELD_LENGTH
            : typeof v === 'number' ? Number.isFinite(v)
            : typeof v === 'boolean'
        if (!ok) continue
        out[k] = v
        n++
    }
    return out
}

export class GrantManager extends EventTarget {
    /**
     * @param {object} opts
     * @param {object} opts.group - the acequia Group the app's viewers are in
     * @param {string} opts.aud - the app ('geocamera', 'debug-console'): tokens carry it, requests name it
     * @param {Object<string, object>} opts.presets - preset name → grant set ({ grants })
     * @param {string} [opts.defaultPreset] - the first preset by default
     * @param {string} [opts.tag] - peer-token tag for this app's records (default: aud)
     * @param {object} [opts.claims] - the app's own leaf claims ({ type: 'debug-console-grant' })
     * @param {number} [opts.ttlSeconds=86400]
     * @param {() => string} [opts.grantorName] - how the owner is named to viewers
     * @param {(request: object) => Promise<object|null>|object|null} [opts.onRequest] - the app's prompt:
     *   resolve { preset, ttlSeconds?, autoRenew? } to grant, { denied: true } to deny, null or
     *   { queued: true } to leave it pending (the app may call grant()/deny() later)
     * @param {(peerId: string, message: object) => any} [opts.send] - delivery (default group.sendToPeer)
     * @param {string} [opts.storageKey] - where pending and denied persist
     * @param {boolean} [opts.renew=true] - run this app's renewal engine
     * @param {(request: object, existing: object) => boolean} [opts.needsPrompt] - whether a request
     *   from a viewer with a live grant is put to the app anyway (an upgrade: more than it has);
     *   by default a returning viewer gets its grant again
     * @param {(record: object) => object} [opts.responseFields] - the app's own fields on an access
     *   response (GeoCamera: the cameras a grant names)
     */
    constructor(opts) {
        super()
        if (!opts?.group) throw new Error('createGrantManager: a group is required')
        if (!opts.aud) throw new Error('createGrantManager: an audience (aud) is required')
        if (!opts.presets || !Object.keys(opts.presets).length) throw new Error('createGrantManager: presets are required')
        this.group = opts.group
        this.aud = opts.aud
        this.tag = opts.tag || opts.aud
        this.presets = Object.fromEntries(Object.entries(opts.presets).map(([k, v]) => [k, canonicalizeScope(v)]))
        this.defaultPreset = opts.defaultPreset || Object.keys(this.presets)[0]
        this.claims = opts.claims || {}
        this.ttlSeconds = opts.ttlSeconds || 86400
        this.grantorName = opts.grantorName || (() => this.group.displayName || (typeof document !== 'undefined' ? document.title : 'acequia'))
        this.onRequest = opts.onRequest || null
        this._send = opts.send || ((peerId, msg) => this.group.sendToPeer(peerId, msg))
        this.storageKey = opts.storageKey || `${this.aud}.grants.${this.group.groupId || this.group.groupName || 'default'}`
        this._renewEnabled = opts.renew !== false
        this.needsPrompt = opts.needsPrompt || (() => false)
        this.responseFields = opts.responseFields || null

        this.cache = null // PeerTokenCache over this app's minted records, by deviceId
        this.pending = new Map() // key → request
        this.denied = new Map() // key → request + deniedAt, asks, lastAskedAt
        this.sessions = new Map() // key → peerId (the viewer's tab)
        this.requestStatuses = new Map() // HTTP requestId → { status, key, response? }
        this._prompting = new Set()
        this._granting = new Map() // key → the grant() in flight for it
        this._lastUseWrite = new Map()
        this._timers = []
        this._unsubs = []
    }

    // ── lifecycle ────────────────────────────────────────────────────

    /** Load the records, pending and denied lists; start renewal. */
    async start() {
        await acequiaReady()
        this.cache = new peerTokens.PeerTokenCache({ origin: 'minted', groupName: this._groupName(), tags: [this.tag], status: 'active' })
        await this.cache.hydrate()
        this._load()
        // A record still live whose grant the wallet has marked revoked (learned
        // in another tab, or before this manager listened): revoked now, so it's
        // not handed out again (issues ledger item 17)
        await this._reconcileRevoked().catch(() => {})
        this._unsubs.push(peerTokens.onTokenChange(({ record }) => {
            if (record?.origin === 'minted' && record?.tags?.includes(this.tag)) this._changed()
        }))
        // A grant of ours found revoked on the server (a revocation made from
        // the dashboard or another browser): its record follows, and its holder
        // hears so, rather than being let in over WebRTC and refused on routes
        this._unsubs.push(grants.onGrantRevokedElsewhere?.((anchorHash) => {
            this._revokedElsewhere(anchorHash).catch(() => {})
        }) || (() => {}))
        if (this._renewEnabled) {
            this._timers.push(setInterval(() => this._renewDue().catch(() => {}), 60 * 1000))
            grants.startGrantSync?.()
        }
        this._changed()
        return this
    }

    destroy() {
        for (const t of this._timers) clearInterval(t)
        for (const u of this._unsubs) { try { u() } catch {} }
        this.cache?.destroy?.()
        this._timers = []
        this._unsubs = []
    }

    _groupName() {
        return this.group.groupId || this.group.groupName || null
    }

    _changed() {
        this.dispatchEvent(new CustomEvent('change'))
    }

    _emit(type, detail) {
        this.dispatchEvent(new CustomEvent(type, { detail }))
        this._changed()
    }

    // ── persistence (pending, denied) ────────────────────────────────

    _load() {
        if (!hasStorage()) return
        try {
            const raw = JSON.parse(localStorage.getItem(this.storageKey) || '{}')
            const cutoff = Date.now() - PENDING_MAX_AGE
            for (const [k, r] of Object.entries(raw.pending || {})) if ((r.requestedAt || 0) > cutoff) this.pending.set(k, r)
            for (const [k, r] of Object.entries(raw.denied || {})) this.denied.set(k, r)
        } catch { /* a corrupt entry starts empty */ }
    }

    _persist() {
        if (!hasStorage()) return
        try {
            localStorage.setItem(this.storageKey, JSON.stringify({
                pending: Object.fromEntries(this.pending),
                denied: Object.fromEntries(this.denied),
            }))
        } catch { /* storage full or blocked */ }
    }

    // ── reading ──────────────────────────────────────────────────────

    /** This app's grant records in its group (live and lapsed), newest first. */
    async list({ includeRevoked = false } = {}) {
        const rows = await peerTokens.listPeerTokens({ origin: 'minted', groupName: this._groupName() || undefined, tags: [this.tag] }).catch(() => [])
        return rows.filter((r) => includeRevoked || r.status !== 'revoked')
            .sort((a, b) => Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0))
    }

    /** The live grant record for a requester (deviceId), if any. */
    get(key) {
        return this.cache?.get(key) || null
    }

    /** Whether a record still lets its viewer in: unexpired, or renewable. */
    isLive(record) {
        if (!record || record.status !== 'active') return false
        return !record.expiresAt || Date.parse(record.expiresAt) > Date.now() || !!record.autoRenew
    }

    /** The preset a grant set is (by equality with a preset), or null. */
    presetOf(scope) {
        if (!scope || typeof scope !== 'object') return null
        const key = JSON.stringify(canonicalizeScope(scope))
        for (const [name, set] of Object.entries(this.presets)) if (JSON.stringify(set) === key) return name
        return null
    }

    /** Whether a verified grant set gives a privilege on a path (as routes check it). */
    allows(scope, method, path) {
        return !!scope && checkScopeAccess(method, path, scope).allowed
    }

    // ── requests ─────────────────────────────────────────────────────

    /** Whether a peer message is for this app: it names this audience. */
    isForMe(data) {
        return data?.aud === this.aud
    }

    /**
     * Handle a peer message for the grant flow: accessRequest and tokenPresent.
     * @returns {Promise<boolean>} whether it was this manager's to handle
     */
    async handleMessage(peerId, data) {
        if (data?.type === 'accessRequest' && this.isForMe(data)) {
            await this.handleAccessRequest(peerId, data)
            return true
        }
        if (data?.type === 'tokenPresent' && (data.aud ? data.aud === this.aud : this.cache?.has(data.deviceId))) {
            await this.handleTokenPresent(peerId, data)
            return true
        }
        return false
    }

    _requestFrom(peerId, data) {
        const key = data.deviceId || data.userId || peerId
        return {
            // The app's own request fields first, so nothing below can be
            // overridden by them (see appFieldsOf).
            ...appFieldsOf(data),
            key,
            deviceId: data.deviceId || null,
            userId: data.userId || null,
            handle: data.handle || null,
            deviceName: data.deviceName || null,
            displayName: data.viewerName || data.displayName || data.handle || 'Unknown peer',
            preset: this.presets[data.scope] ? data.scope : this.defaultPreset,
            camera: data.camera || null,
            upgradeFrom: data.upgradeFrom || null,
            viewerId: data.viewerId || peerId,
            requestId: data.requestId || null,
            requestedAt: Date.now(),
        }
    }

    async handleAccessRequest(peerId, data) {
        const request = this._requestFrom(peerId, data)
        const { key } = request
        this.sessions.set(key, request.viewerId)

        // A returning viewer with a live grant gets it again — unless it asks
        // for more than it has (the app's needsPrompt), which is put to the app
        const existing = this.get(key)
        if (this.isLive(existing)) {
            if (!this.needsPrompt(request, existing)) {
                await this.noteViewer(existing, request)
                const record = this._lapsed(existing) ? await this._renewRecord(existing) : existing
                await this._deliver(request.viewerId, record, request.requestId)
                return { granted: true, record, auto: true }
            }
            request.isUpgrade = true
        }

        // A denied requester is refused without asking
        const denied = this.denied.get(key)
        if (denied) {
            Object.assign(denied, { ...request, deniedAt: denied.deniedAt, asks: (denied.asks || 0) + 1, lastAskedAt: Date.now() })
            this._persist()
            this._sendDenied(request.viewerId, request.requestId)
            this._changed()
            return { denied: true }
        }

        this.pending.set(key, request)
        this._persist()
        this._emit('request', request)
        // Not awaited: the owner may take minutes to answer
        this.prompting = this._prompt(key).catch((err) => console.warn(`[grantManager:${this.aud}] prompt failed:`, err.message))
        return { pending: true }
    }

    /** Ask the app (onRequest), once at a time per requester, and act on the answer. */
    async _prompt(key) {
        if (!this.onRequest || this._prompting.has(key)) return
        const request = this.pending.get(key)
        if (!request) return
        this._prompting.add(key)
        try {
            const answer = await this.onRequest({ ...request, identity: identityOf(request), existing: this.get(key), isUpgrade: !!request.isUpgrade })
            if (!this.pending.has(key)) return // answered elsewhere meanwhile
            if (answer?.denied) await this.deny(key)
            else if (answer && (answer.preset || answer.scope) && !answer.queued) await this.grant(key, answer)
        } finally {
            this._prompting.delete(key)
        }
    }

    /**
     * Put the pending requests to the app again (after a reload: the prompts
     * they had are gone), newest first, one at a time.
     */
    async promptPending() {
        const keys = [...this.pending].sort((a, b) => (b[1].requestedAt || 0) - (a[1].requestedAt || 0)).map(([k]) => k)
        for (const key of keys) {
            if (!this.pending.has(key)) continue
            const request = this.pending.get(key)
            if (this.isLive(this.get(key))) request.isUpgrade = true
            await this._prompt(key).catch((err) => console.warn(`[grantManager:${this.aud}] prompt failed:`, err.message))
        }
    }

    /**
     * Grant a pending request (or a requester by key with a request object).
     * The preset names the grant; `scope` (the app's own grant set, GeoCamera's
     * preset over chosen cameras) is what the token carries when given.
     */
    async grant(key, opts = {}) {
        // One grant at a time per requester: a second approval while the first
        // is minting (a double click, or the banner and the grants list) used
        // to run beside it — each removed the other's fresh record as "the one
        // it had", and the first then delivered a record the store no longer
        // held (a null read, no response to the viewer, a grant in the list)
        const inFlight = this._granting.get(key)
        if (inFlight) return inFlight
        const p = this._grantOnce(key, opts).finally(() => { if (this._granting.get(key) === p) this._granting.delete(key) })
        this._granting.set(key, p)
        return p
    }

    async _grantOnce(key, { preset, scope: customScope, ttlSeconds, autoRenew = true, request } = {}) {
        const req = request || this.pending.get(key)
        if (!req) throw new Error(`grant: no request from ${key}`)
        const presetName = this.presets[preset] ? preset : (this.presets[req.preset] ? req.preset : this.defaultPreset)
        const scope = customScope ? canonicalizeScope(customScope) : this.presets[presetName]
        const ttl = ttlSeconds || this.ttlSeconds
        const who = identityOf(req)
        const name = grantRecordName(who, presetName)

        // One grant per requester: a new one replaces the one it had
        for (const old of await this.list({ includeRevoked: true })) {
            if (old.deviceId === (req.deviceId || key)) await peerTokens.removePeerToken(old.id, MINTED).catch(() => {})
        }

        const signed = await this._sign({ grantee: req.deviceId || key, scope, ttlSeconds: ttl, name, preset: presetName })
        const expiresAt = new Date(Date.now() + ttl * 1000).toISOString()
        const id = await peerTokens.createPeerToken({
            ...(signed.leafId ? { id: signed.leafId } : {}),
            jwt: signed.token,
            name,
            scope,
            tags: [this.tag, presetName],
            origin: 'minted',
            deviceId: req.deviceId || key,
            groupName: this._groupName(),
            expiresAt,
            autoRenew,
            ttlSeconds: ttl,
            granteeHandle: who.handle,
            granteeUserId: who.userId,
            granteeDeviceName: who.deviceName,
            granteeApp: who.app,
        })
        await peerTokens.updatePeerToken(id, {
            ...(signed.anchorHash ? { anchorHash: signed.anchorHash, anchors: signed.anchors } : {}),
            granteeDeviceName: who.deviceName || null,
            granteeApp: who.app || null,
            lastUsed: new Date().toISOString(),
        }, MINTED).catch(() => {})
        await this.cache?.hydrate?.().catch?.(() => {})

        this.pending.delete(key)
        this.denied.delete(key)
        this._persist()
        const record = await peerTokens.getPeerToken(id)
        if (!record) throw new Error(`grant: the record ${id} for ${key} is gone before delivery`)
        if (req.viewerId) await this._deliver(req.viewerId, record, req.requestId)
        this._settleHttp(key, { status: 'granted', record })
        this._emit('grant', { key, preset: presetName, record })
        return record
    }

    /** Deny a requester: refused now, and its later requests refused without asking. */
    async deny(key) {
        const req = this.pending.get(key) || this.denied.get(key)
        this.pending.delete(key)
        this.denied.set(key, { ...(req || { key }), deniedAt: Date.now(), asks: 0, lastAskedAt: null })
        this._persist()
        if (req?.viewerId) this._sendDenied(req.viewerId, req.requestId)
        this._settleHttp(key, { status: 'denied' })
        this._emit('deny', { key })
    }

    /** Off the denied list: its last request goes back to pending. */
    allowAsking(key) {
        const d = this.denied.get(key)
        if (!d) return
        this.denied.delete(key)
        const { deniedAt, asks, lastAskedAt, ...request } = d
        this.pending.set(key, { ...request, requestedAt: Date.now() })
        this._persist()
        this._changed()
    }

    /** Drop a pending request without denying it (it may ask again). */
    dismiss(key) {
        if (this.pending.delete(key)) {
            this._persist()
            this._changed()
        }
    }

    /** Revoke a requester's grant (by key, or by the record's id). */
    async revoke(keyOrId) {
        const record = this.get(keyOrId) || (await this.list()).find((r) => r.id === keyOrId)
        if (!record) return false
        await peerTokens.revokePeerToken(record.id, MINTED).catch(() => {})
        if (record.anchorHash) await grants.revokeGrant(record.anchorHash, { reason: `revoked by the ${this.aud} owner` }).catch(() => {})
        this._tellRevoked(record)
        return true
    }

    /** Live records whose grant the wallet marks revoked: revoked, and their holders told. */
    async _reconcileRevoked() {
        if (!grants.isMarkedRevoked) return
        for (const record of await this.list()) {
            if (record.anchorHash && await grants.isMarkedRevoked(record.anchorHash)) {
                console.log(`[grantManager:${this.aud}] ${record.name || record.deviceId || 'a grant'}: revoked on the server, marked here — revoking the record`)
                await this._revokedElsewhere(record.anchorHash)
            }
        }
    }

    /** A grant of ours the server has revoked: the record marked so, no revocation of our own (the server's is in force). */
    async _revokedElsewhere(anchorHash) {
        const record = (await this.list()).find((r) => r.anchorHash === anchorHash && r.status !== 'revoked')
        if (!record) return
        await peerTokens.revokePeerToken(record.id, MINTED).catch(() => {})
        this._tellRevoked(record)
        this._changed()
    }

    /** The holder's tab, if connected, hears its grant is revoked; the session ends. */
    _tellRevoked(record) {
        const peerId = this.sessions.get(record.deviceId) || this._peerIdFor(record.deviceId)
        if (peerId) {
            this._safeSend(peerId, {
                ...buildTokenRevocation({ cameraDeviceId: getDeviceId(), cameraUserId: getUserId(), cameraName: this.grantorName(), tokenId: record.id }),
                aud: this.aud,
            })
        }
        this.sessions.delete(record.deviceId)
        this._emit('revoke', { key: record.deviceId, record })
    }

    /** Remove a grant record (by key or id) — revoked first, so a lapsed one can't be renewed. */
    async remove(keyOrId) {
        const record = (await this.list({ includeRevoked: true })).find((r) => r.id === keyOrId) || this.get(keyOrId)
        if (!record) return false
        if (record.status !== 'revoked') await this.revoke(record.id)
        await peerTokens.removePeerToken(record.id, MINTED).catch(() => {})
        await this.cache?.hydrate?.().catch?.(() => {})
        this._changed()
        return true
    }

    async setAutoRenew(key, autoRenew) {
        const record = this.get(key)
        if (!record) return
        await peerTokens.updatePeerToken(record.id, { autoRenew: !!autoRenew }, MINTED)
        this._changed()
    }

    // ── presentation and verification ────────────────────────────────

    /**
     * Verify a presented grant: peer-token checks with this app's audience, and
     * an anchor-model grant against its anchor (offline: the wallet, or the
     * anchors presented with it).
     * @returns {Promise<{ valid: boolean, reason?: string, record?: object, claims?: object, scope?: object }>}
     */
    async verify(tokenOrId, { anchors = [] } = {}) {
        const result = await peerTokens.verifyPeerToken(tokenOrId, { aud: this.aud })
        // A raw token carries no record; this app's own record for it, by its
        // anchor or the leaf itself
        if (!result.record && result.claims) {
            const rows = await this.list({ includeRevoked: true })
            result.record = rows.find((r) => (result.claims.anchor && r.anchorHash === result.claims.anchor) || r.jwt === tokenOrId) || null
            if (result.record?.status === 'revoked') return { ...result, valid: false, reason: 'revoked' }
        }
        if (!result.valid || !result.claims?.anchor) return result
        const g = await grants.verifyGrant(result.record?.jwt || tokenOrId, { aud: this.aud, anchors })
        if (g.valid) return { ...result, scope: g.scope }
        if (g.expired && result.record?.autoRenew) return { ...result, valid: false, reason: 'expired' }
        return { ...result, valid: false, reason: g.error || 'invalid grant' }
    }

    async handleTokenPresent(peerId, data) {
        const result = await this.verify(data.tokenId || data.token, { anchors: data.anchors || [] })
        const key = result.record?.deviceId || data.deviceId
        const to = data.viewerId || peerId
        if (key) this.sessions.set(key, to)
        if (result.valid && result.record) {
            await this.noteViewer(result.record, data)
            await this._deliver(to, result.record, data.requestId)
            return { valid: true }
        }
        // A token this browser signed, with no record for it (the store was
        // cleared): the record comes back from the token
        if (result.valid && !result.record && result.claims?.grantee && result.claims.grantee === data.deviceId) {
            const record = await this._rebuildRecord(data, result)
            if (record) {
                await this._deliver(to, record, data.requestId)
                return { valid: true, rebuilt: true }
            }
        }
        // An expired token for a renewing grant: the grant it belongs to — not a
        // new leaf. The record may already hold a live token (renewed on its
        // timer, or for this viewer's last stale presentation), and a viewer
        // that keeps presenting the old one would otherwise mint a leaf per
        // presentation (production-cpu, 2026-10-20: 18 grants re-minted
        // together on every reconnect). Mint only when the grant itself lapsed.
        if (result.reason === 'expired' && result.record?.autoRenew) {
            const lapsed = this._lapsed(result.record)
            const record = lapsed ? await this._renewRecord(result.record) : result.record
            await this._deliver(to, record, data.requestId)
            return { valid: true, renewed: lapsed }
        }
        // A stale token from a viewer that has a live grant here (an old one it
        // kept): the grant it has, as its request would get
        const existing = data.deviceId && this.get(data.deviceId)
        if (this.isLive(existing)) {
            const record = this._lapsed(existing) ? await this._renewRecord(existing) : existing
            await this._deliver(to, record, data.requestId)
            return { valid: true, fallback: true }
        }
        this._sendDenied(to, data.requestId, result.reason || 'invalid')
        return { valid: false, reason: result.reason }
    }

    /** The record for a verified token this browser has no record of. */
    async _rebuildRecord(data, result) {
        const claims = result.claims
        const scope = result.scope || (claims.scope && typeof claims.scope === 'object' ? canonicalizeScope(claims.scope) : null)
        if (!scope) return null
        const preset = (claims.preset && this.presets[claims.preset]) ? claims.preset : (this.presetOf(scope) || this.defaultPreset)
        const who = identityOf({ ...data, app: data.viewerName || data.displayName || null })
        const ttl = claims.exp && claims.iat ? Math.max(300, claims.exp - claims.iat) : this.ttlSeconds
        const expiresAt = new Date((claims.exp || Math.floor(Date.now() / 1000) + ttl) * 1000).toISOString()
        const id = await peerTokens.createPeerToken({
            jwt: result.record?.jwt || (typeof data.token === 'string' ? data.token : null),
            name: claims.granteeName || grantRecordName(who, preset),
            scope,
            tags: [this.tag, preset],
            origin: 'minted',
            deviceId: data.deviceId,
            groupName: this._groupName(),
            expiresAt,
            autoRenew: true,
            ttlSeconds: ttl,
            granteeHandle: who.handle,
            granteeUserId: who.userId,
            granteeDeviceName: who.deviceName,
            granteeApp: who.app,
        })
        await peerTokens.updatePeerToken(id, {
            ...(claims.anchor ? { anchorHash: claims.anchor, anchors: data.anchors || [] } : {}),
            granteeDeviceName: who.deviceName || null,
            granteeApp: who.app || null,
            lastUsed: new Date().toISOString(),
        }, MINTED).catch(() => {})
        await this.cache?.hydrate?.().catch?.(() => {})
        console.log(`[grantManager:${this.aud}] rebuilt the record of a presented grant for ${data.deviceId}`)
        this._changed()
        return peerTokens.getPeerToken(id)
    }

    _lapsed(record) {
        return !!record?.expiresAt && Date.parse(record.expiresAt) <= Date.now()
    }

    // ── identity and last use ────────────────────────────────────────

    /** Keep who the viewer is on its grant (parts newly known), and its use. */
    async noteViewer(record, request) {
        const who = identityOf(request)
        const updates = {}
        if (who.handle && who.handle !== record.granteeHandle) updates.granteeHandle = who.handle
        if (who.deviceName && who.deviceName !== record.granteeDeviceName) updates.granteeDeviceName = who.deviceName
        if (who.userId && who.userId !== record.granteeUserId) updates.granteeUserId = who.userId
        if (who.app && who.app !== record.granteeApp) updates.granteeApp = who.app
        if (Object.keys(updates).length) {
            await peerTokens.updatePeerToken(record.id, { ...updates, lastUsed: new Date().toISOString() }, MINTED).catch(() => {})
            this._lastUseWrite.set(record.id, Date.now())
            return
        }
        await this.noteUse(record)
    }

    /** A grant was used now; written at most once a minute. */
    async noteUse(record) {
        if (!record?.id) return
        const last = this._lastUseWrite.get(record.id) || 0
        if (Date.now() - last < USE_WRITE_INTERVAL) return
        this._lastUseWrite.set(record.id, Date.now())
        await peerTokens.updatePeerToken(record.id, { lastUsed: new Date().toISOString() }, MINTED).catch(() => {})
    }

    // ── minting and renewal ──────────────────────────────────────────

    async _sign({ grantee, scope, ttlSeconds, name, preset }) {
        // The preset's name rides in the leaf: a record rebuilt from a presented
        // token (the store cleared) keeps its label
        const claims = { ...this.claims, granteeName: name, grantorName: this.grantorName(), ...(preset ? { preset } : {}) }
        const g = await grants.mintGrant({ scope, aud: this.aud, grantee, ttlSeconds, name, claims })
        return { token: g.leafJwt, leafId: g.leafId, anchorHash: g.anchorHash, anchors: g.anchors }
    }

    /** A new token for a grant: a new leaf under its anchor (a peer-token grant from before becomes one). */
    async _reMint(record) {
        const ttl = record.ttlSeconds || this.ttlSeconds
        if (record.anchorHash) {
            try {
                const r = await grants.renewGrant(record.anchorHash, { ttlSeconds: ttl })
                return { jwt: r.token, expiresAt: r.expiresAt }
            } catch (err) {
                // The anchor isn't in this wallet (a record rebuilt from a
                // presented token): a fresh grant under a new anchor
                if (!/no such grant/i.test(err.message)) throw err
            }
        }
        const preset = record.tags?.find((t) => this.presets[t]) || this.presetOf(record.scope) || this.defaultPreset
        const scope = record.scope && typeof record.scope === 'object' ? record.scope : this.presets[preset]
        const s = await this._sign({ grantee: record.deviceId, scope, ttlSeconds: ttl, name: record.name, preset })
        await peerTokens.updatePeerToken(record.id, { anchorHash: s.anchorHash, anchors: s.anchors, scope }, MINTED).catch(() => {})
        return { jwt: s.token, expiresAt: new Date(Date.now() + ttl * 1000).toISOString() }
    }

    async _renewRecord(record) {
        const { jwt, expiresAt } = await this._reMint(record)
        await peerTokens.renewPeerToken(record.id, jwt, expiresAt, MINTED)
        const renewed = await peerTokens.getPeerToken(record.id)
        this._emit('renew', { key: record.deviceId, record: renewed })
        return renewed
    }

    /** Renew this app's grants nearing expiry (10% of their TTL, or 5 minutes), and push them. */
    async _renewDue() {
        for (const record of await this.list()) {
            if (!record.autoRenew || !record.expiresAt) continue
            const remaining = Date.parse(record.expiresAt) - Date.now()
            if (remaining <= 0) continue
            const threshold = Math.max((record.ttlSeconds || 3600) * 1000 * 0.1, 5 * 60 * 1000)
            if (remaining >= threshold) continue
            try {
                const renewed = await this._renewRecord(record)
                const peerId = this.sessions.get(record.deviceId) || this._peerIdFor(record.deviceId)
                if (peerId) {
                    this._safeSend(peerId, {
                        ...buildTokenRenewal({ token: renewed.jwt, tokenId: renewed.id, expiresAt: renewed.expiresAt, cameraDeviceId: getDeviceId(), cameraUserId: getUserId(), cameraName: this.grantorName() }),
                        aud: this.aud,
                    })
                }
            } catch (err) {
                console.warn(`[grantManager:${this.aud}] renewal failed for ${record.id}:`, err.message)
            }
        }
    }

    // ── delivery ─────────────────────────────────────────────────────

    /** The access response for a grant: the grant set, its preset as a label, token, audience, anchors. */
    response(record, requestId) {
        const preset = record.tags?.find((t) => this.presets[t]) || this.presetOf(record.scope) || this.defaultPreset
        return {
            ...buildAccessResponse({
                cameraId: getInstanceId(),
                cameraDeviceId: getDeviceId(),
                cameraUserId: getUserId(),
                cameraName: this.grantorName(),
                granted: true,
                scope: record.scope && typeof record.scope === 'object' ? record.scope : this.presets[preset],
                preset,
                token: record.jwt,
                tokenId: record.id,
                expiresAt: record.expiresAt,
                aud: this.aud,
            }),
            aud: this.aud,
            ...(record.anchors ? { anchors: record.anchors } : {}),
            ...(this.responseFields ? this.responseFields(record) : {}),
            ...(requestId ? { requestId } : {}),
        }
    }

    async _deliver(peerId, record, requestId) {
        if (peerId) this._safeSend(peerId, this.response(record, requestId))
        this._emit('deliver', { key: record.deviceId, record, peerId: peerId || null })
    }

    _sendDenied(peerId, requestId, reason = 'denied') {
        if (!peerId) return
        this._safeSend(peerId, {
            ...buildAccessResponse({ cameraId: getInstanceId(), cameraDeviceId: getDeviceId(), cameraName: this.grantorName(), granted: false, reason, message: reason === 'denied' ? 'Access denied by the owner' : undefined, aud: this.aud }),
            ...(requestId ? { requestId } : {}),
        })
    }

    _safeSend(peerId, msg) {
        try {
            const r = this._send(peerId, msg)
            if (r?.catch) r.catch(() => {})
        } catch { /* the viewer re-asks or re-presents */ }
    }

    _peerIdFor(deviceId) {
        const peers = this.group.peersList || {}
        for (const [id, info] of Object.entries(peers)) if (info?.deviceId === deviceId) return id
        return null
    }

    // ── HTTP routes ──────────────────────────────────────────────────

    _settleHttp(key, outcome) {
        for (const [requestId, st] of this.requestStatuses) {
            if (st.key !== key || st.status !== 'pending') continue
            st.status = outcome.status
            if (outcome.record) st.response = this._httpGrant(outcome.record)
        }
    }

    _httpGrant(record) {
        const r = this.response(record)
        return { token: r.token, tokenId: r.tokenId, scope: r.scope, preset: r.preset, anchors: r.anchors, expiresAt: r.expiresAt, aud: this.aud }
    }

    /**
     * The HTTP side of the flow, as acequia routes under a prefix
     * ('/<instanceId>'): for a client outside the group (curl, a service) or
     * a route explorer.
     *   POST auth/request   { deviceId, displayName, handle?, deviceName?, userId?, scope? }
     *                       → 200 granted (a live grant), 403 denied, 202 pending { requestId }
     *   GET  auth/status/:requestId → { status, …grant when granted }
     *   POST auth/present   { token | tokenId, anchors? } → 200 confirmed (renewed if lapsed) | 403
     *   GET  auth/verify?token= → { valid, scope, expiresAt }
     *   GET  auth/grants, DELETE auth/grant/:key — the owner only: a token the owner
     *                       signed for itself (any of this browser's identities), naming no grantee
     */
    routes(prefix = `/${getInstanceId()}`) {
        const json = (status, body) => ({ status, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
        const parse = (data) => { try { return typeof data.body === 'string' ? JSON.parse(data.body) : (data.body || {}) } catch { return null } }
        const owner = async (data) => {
            const c = data.auth?.claims
            return !!c && !c.grantee && (await ownUserIds()).includes(c.sub)
        }
        return [
            {
                pattern: `${prefix}/auth/request`,
                metadata: { auth: { required: false } },
                handler: async (data) => {
                    const b = parse(data)
                    if (!b?.deviceId || !(b.displayName || b.handle)) return json(400, { error: 'deviceId and displayName are required' })
                    const requestId = b.requestId || (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`)
                    // Recorded first: an answer that comes at once still lands here
                    this.requestStatuses.set(requestId, { status: 'pending', key: b.deviceId, requestedAt: Date.now() })
                    const out = await this.handleAccessRequest(b.instanceId || null, { ...b, viewerName: b.displayName, viewerId: b.instanceId || null, requestId, aud: this.aud })
                    if (out.granted) { this.requestStatuses.set(requestId, { status: 'granted', key: b.deviceId, response: this._httpGrant(out.record) }); return json(200, { status: 'granted', requestId, ...this._httpGrant(out.record) }) }
                    if (out.denied) { this.requestStatuses.set(requestId, { status: 'denied', key: b.deviceId }); return json(403, { status: 'denied', requestId, reason: 'denied' }) }
                    return json(202, { status: 'pending', requestId })
                },
            },
            {
                pattern: `${prefix}/auth/status/:requestId`,
                metadata: { auth: { required: false } },
                handler: async (data) => {
                    const st = this.requestStatuses.get(data.pathParams?.requestId)
                    if (!st) return json(404, { error: 'Unknown request' })
                    return json(200, { status: st.status, ...(st.response || {}) })
                },
            },
            {
                pattern: `${prefix}/auth/present`,
                metadata: { auth: { required: false } },
                handler: async (data) => {
                    const b = parse(data) || {}
                    const r = await this.verify(b.tokenId || b.token, { anchors: b.anchors || [] })
                    if (r.valid && r.record) { await this.noteUse(r.record); return json(200, { status: 'confirmed', ...this._httpGrant(r.record) }) }
                    // As handleTokenPresent: the grant's live token if it has one, a new leaf only once it lapsed
                    if (r.reason === 'expired' && r.record?.autoRenew) return json(200, { status: 'renewed', ...this._httpGrant(this._lapsed(r.record) ? await this._renewRecord(r.record) : r.record) })
                    return json(403, { status: 'invalid', reason: r.reason })
                },
            },
            {
                pattern: `${prefix}/auth/verify`,
                metadata: { auth: { required: false } },
                handler: async (data) => {
                    const q = data.query || Object.fromEntries(new URLSearchParams((data.path || '').split('?')[1] || ''))
                    const r = await this.verify(q.tokenId || q.token || '')
                    return json(200, { valid: !!r.valid, reason: r.reason || null, scope: r.scope || null, expiresAt: r.record?.expiresAt || null })
                },
            },
            {
                pattern: `${prefix}/auth/grants`,
                metadata: { auth: { required: true, enforce: true, aud: this.aud } },
                handler: async (data) => {
                    if (!await owner(data)) return json(403, { error: 'Grant management is the owner\'s: present a token you signed for yourself, not a grant' })
                    const list = (await this.list()).map((r) => ({
                        key: r.deviceId, id: r.id, name: r.name, preset: r.tags?.find((t) => this.presets[t]) || null,
                        expiresAt: r.expiresAt, autoRenew: !!r.autoRenew, lastUsed: r.lastUsed || null, identity: identityOf(r),
                    }))
                    return json(200, { grants: list, pending: [...this.pending.values()], denied: [...this.denied.values()] })
                },
            },
            {
                pattern: `${prefix}/auth/grant/:key`,
                metadata: { auth: { required: true, enforce: true, aud: this.aud } },
                handler: async (data) => {
                    if (!await owner(data)) return json(403, { error: 'Grant management is the owner\'s: present a token you signed for yourself, not a grant' })
                    const ok = await this.revoke(data.pathParams?.key)
                    return ok ? json(200, { status: 'revoked', key: data.pathParams?.key }) : json(404, { error: 'No grant' })
                },
            },
        ]
    }
}

/**
 * Create an app's grant manager, and start it.
 * @param {object} opts - see GrantManager
 * @returns {Promise<GrantManager>}
 */
export async function createGrantManager(opts) {
    return new GrantManager(opts).start()
}
